/**
 * FormatPickerModal — reusable popup that shows when downloading any report.
 * 
 * Usage:
 *   <FormatPickerModal
 *     reportType="billing"
 *     params={{ month: 5, year: 2026, kitchen_id: "xxx" }}
 *     onClose={() => setShowPicker(false)}
 *   />
 */
import React, { useState, useEffect } from "react";
import { X, Download, FileText, Loader, Check } from "lucide-react";
import { formatError } from "../utils/api";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("manna_access_token")}` } });

// Built-in formats that always appear (backed by existing endpoints)
const BUILTIN_FORMATS = {
  billing: [
    {
      id: "__builtin_voucher",
      name: "DEO Voucher Format",
      description: "Day-wise: Primary (1-5), Upper 6-8, Upper 9-10 — 3 separate sheets",
      endpoint: (params) => `/api/accounts/billing-voucher/${params.period}${params.kitchen_id ? `?kitchen_id=${params.kitchen_id}` : ""}`,
      filename: (params) => `Mannatrust_Voucher_${params.period}.xlsx`,
    },
    {
      id: "__builtin_schoolwise",
      name: "DEO School-wise Billing",
      description: "School-by-school with caste breakdown (General/SC/ST)",
      endpoint: (params) => `/api/accounts/billing-export/${params.period}${params.kitchen_id ? `?kitchen_id=${params.kitchen_id}` : ""}`,
      filename: (params) => `Mannatrust_Billing_${params.period}.xlsx`,
    },
  ],
};

export default function FormatPickerModal({ reportType, params, onClose, title }) {
  const [formats, setFormats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadFormats();
  }, [reportType]);

  const loadFormats = async () => {
    setLoading(true);
    try {
      // Load saved formats
      const r = await axios.get(`${API}/api/report-formats?report_type=${reportType}`, auth());
      const savedFormats = (r.data || []).map(f => ({
        id: f.id,
        name: f.name,
        description: f.description,
        type: "saved",
      }));
      // Combine with built-ins
      const builtins = (BUILTIN_FORMATS[reportType] || []).map(f => ({ ...f, type: "builtin" }));
      const all = [...builtins, ...savedFormats];
      setFormats(all);
      if (all.length > 0) setSelected(all[0].id);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('loadFormats failed:', e);
    }
    setLoading(false);
  };

  const handleDownload = async () => {
    if (!selected) return;
    setDownloading(true); setError("");
    const fmt = formats.find(f => f.id === selected);
    if (!fmt) { setError("Format not found"); setDownloading(false); return; }

    try {
      let url, filename;
      if (fmt.type === "builtin") {
        url = `${API}${fmt.endpoint(params)}`;
        filename = fmt.filename(params);
      } else {
        // Saved format — use generate endpoint
        url = null;
        const response = await fetch(`${API}/api/report-formats/${fmt.id}/generate`, {
          method: "POST",
          headers: { ...auth().headers, "Content-Type": "application/json" },
          body: JSON.stringify(params),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({ detail: "Generation failed" }));
          throw new Error(err.detail || "Generation failed");
        }
        const blob = await response.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${fmt.name.replace(/\s/g, "_")}_${params.period || "report"}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
        setSuccess(true);
        setTimeout(() => { setSuccess(false); onClose(); }, 1500);
        setDownloading(false);
        return;
      }

      // Builtin format
      const token = localStorage.getItem("manna_access_token");
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); }, 1500);
    } catch (e) {
      setError(formatError(e));
    }
    setDownloading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1DB]">
          <div>
            <h3 className="font-bold text-[#3A322C]">{title || "Choose Download Format"}</h3>
            <p className="text-xs text-[#8A7E73] mt-0.5">Select the format to download the report in</p>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {loading ? (
            <div className="text-center py-6"><Loader size={20} className="animate-spin text-[#E8956D] mx-auto" /></div>
          ) : formats.length === 0 ? (
            <div className="text-center py-6 text-[#8A7E73] text-sm">
              No formats available. Create one in Settings → Report Formats.
            </div>
          ) : (
            formats.map(fmt => (
              <label key={fmt.id}
                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${selected === fmt.id ? "border-[#E8956D] bg-[#E8956D]/5" : "border-[#E5E1DB] hover:border-[#E8956D]/40"}`}>
                <input type="radio" name="format" value={fmt.id}
                  checked={selected === fmt.id}
                  onChange={() => setSelected(fmt.id)}
                  className="mt-0.5 accent-[#E8956D]" />
                <div>
                  <p className="font-bold text-[#3A322C] text-sm flex items-center gap-2">
                    {fmt.name}
                    {fmt.type === "builtin" && <span className="text-[10px] bg-[#EDF2EE] text-[#637C64] px-1.5 py-0.5 rounded-full">Built-in</span>}
                    {fmt.type === "saved" && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">Custom</span>}
                  </p>
                  {fmt.description && <p className="text-xs text-[#8A7E73] mt-0.5">{fmt.description}</p>}
                </div>
              </label>
            ))
          )}

          {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-lg px-3 py-2 text-sm">{error}</div>}
          {success && <div className="bg-[#EDF2EE] text-[#637C64] rounded-lg px-3 py-2 text-sm flex items-center gap-2"><Check size={13}/> Downloaded successfully!</div>}

          <div className="pt-2 text-xs text-[#8A7E73] flex items-center gap-1">
            <FileText size={11} />
            Need a new format? Go to <strong>Settings → Report Formats</strong> to create one.
          </div>
        </div>

        <div className="flex gap-3 px-5 pb-5">
          <button onClick={onClose} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
          <button onClick={handleDownload} disabled={!selected || downloading || formats.length === 0}
            className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {downloading ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
            {downloading ? "Downloading..." : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
}
